import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import JobCard from '../components/JobCard';
import { API_URL, formatDate, getStatusBadgeClass, licenseOptions } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Bookmark,
  FileText,
  User,
  Settings,
  Loader2,
  Upload,
  MapPin,
  Phone,
  Calendar,
  Building2,
  ExternalLink,
  Award,
  GraduationCap,
  Trash2,
  FileCheck,
  Archive,
  MessageSquare,
  Sparkles,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import MessagesPanel from '../components/MessagesPanel';
import { useJobSeekerCtx } from './JobSeekerContext';


const JobSeekerTabApplications = () => {
  const { applications, deletingApp, handleArchiveApplication, handleWithdrawApplication, fetchData } = useJobSeekerCtx();

  const STATUS_LABELS = {
    application_started: 'Application Started',
    applied_externally: 'Applied Externally',
  };
  const statusLabel = (s) => STATUS_LABELS[s] || (s.charAt(0).toUpperCase() + s.slice(1));
  const statusClass = (s) => {
    if (s === 'application_started') return 'bg-amber-100 text-amber-700';
    if (s === 'applied_externally') return 'bg-blue-100 text-blue-700';
    return getStatusBadgeClass(s);
  };

  const handleMarkCompleted = async (applicationId) => {
    try {
      await axios.put(`${API_URL}/applications/${applicationId}/external-complete`, { completed: true });
      toast.success('Marked as completed');
      if (fetchData) fetchData();
    } catch (error) {
      toast.error('Could not update. Please try again.');
    }
  };

  return (
    <>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {applications.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>Company</th>
                        <th>Applied</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => {
                        const isExternal = app.application_type === 'external' || app.status === 'application_started' || app.status === 'applied_externally';
                        const extUrl = app.external_url || app.job?.external_url;
                        return (
                        <tr key={app.application_id}>
                          <td className="font-medium">{app.job?.title || 'N/A'}</td>
                          <td>{app.job?.company_name || 'N/A'}</td>
                          <td>{formatDate(app.created_at)}</td>
                          <td>
                            <Badge className={statusClass(app.status)}>
                              {statusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex items-center gap-1">
                              {isExternal && extUrl && (
                                <a href={extUrl} target="_blank" rel="noopener noreferrer">
                                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" title="Continue on employer site">
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </a>
                              )}
                              {app.status === 'application_started' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkCompleted(app.application_id)}
                                  className="text-green-600 hover:text-green-700"
                                  title="I completed this application"
                                  data-testid={`mark-completed-${app.application_id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Link to={`/jobs/${app.job_id}`}>
                                <Button variant="ghost" size="sm">
                                  View
                                </Button>
                              </Link>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleArchiveApplication(app.application_id)}
                                disabled={deletingApp === app.application_id}
                                className="text-gray-500 hover:text-gray-700"
                                title="Archive"
                              >
                                {deletingApp === app.application_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Archive className="w-4 h-4" />
                                )}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleWithdrawApplication(app.application_id)}
                                disabled={deletingApp === app.application_id}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Withdraw Application"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 mb-2">No applications yet</h3>
                  <p className="text-gray-500 mb-4">Start applying to jobs to track your applications here.</p>
                  <Link to="/jobs">
                    <Button className="bg-primary hover:bg-primary-hover text-white">
                      Browse All Jobs
                    </Button>
                  </Link>
                </div>
              )}
            </div>
    </>
  );
};

export default JobSeekerTabApplications;
